import type { MultiSelectResponse, RenderableQuestion } from '@assessments/shared'
import { cn } from '@/lib/utils'

interface Props {
  question: RenderableQuestion
  value: MultiSelectResponse
  onChange: (response: MultiSelectResponse) => void
  labelledBy: string
  describedBy?: string
  disabled?: boolean
}

/**
 * Checkbox list: several options selectable at once, with the exclusive-option rule
 * (FR-005/006/007) — selecting an exclusive option clears the rest, and selecting any
 * other option clears the exclusive one.
 */
export function MultiSelectQuestion({
  question,
  value,
  onChange,
  labelledBy,
  describedBy,
  disabled,
}: Props) {
  const selected = value.selectedOptionIds
  const exclusiveIds = new Set(question.options.filter((o) => o.isExclusive).map((o) => o.id))

  function toggle(optionId: string, checked: boolean) {
    let next: string[]
    if (!checked) {
      next = selected.filter((id) => id !== optionId)
    } else if (exclusiveIds.has(optionId)) {
      next = [optionId]
    } else {
      next = [...selected.filter((id) => !exclusiveIds.has(id)), optionId]
    }
    onChange({ kind: 'multi', selectedOptionIds: next })
  }

  return (
    <div
      role="group"
      aria-labelledby={labelledBy}
      aria-describedby={describedBy}
      className="mt-3 space-y-2"
    >
      {question.options.map((option) => {
        const isChecked = selected.includes(option.id)
        return (
          <label
            key={option.id}
            className={cn(
              'flex cursor-pointer items-center gap-3 rounded-button border px-3 py-2 text-sm',
              isChecked
                ? 'border-primary bg-primary/5 font-medium text-body'
                : 'border-input text-body',
              disabled && 'cursor-not-allowed opacity-50',
            )}
          >
            <input
              type="checkbox"
              value={option.id}
              checked={isChecked}
              disabled={disabled}
              onChange={(e) => toggle(option.id, e.target.checked)}
              className="h-4 w-4"
            />
            <span>{option.label}</span>
            {option.isExclusive && (
              <span className="ml-auto text-xs uppercase tracking-wide text-subtle">Exclusive</span>
            )}
          </label>
        )
      })}
    </div>
  )
}
